import React from "react";
import { useState, useCallback } from "react";
import { loader } from "graphql.macro";
import PropTypes from "prop-types";
import AddDocumentButton from "./AddDocumentButton";
import InputText from "shared/components/inputs/InputText";
import Button from "shared/components/buttons/Button";
import FormContainer from "shared/components/FormContainer";
import ButtonAdd from "shared/components/buttons/ButtonAdd";
import { useTranslation } from "react-i18next";
import client from "graphql/client";
import { fileSetLabel } from "graphql/mutations/upload";
import { addChapterFile } from "redux/actions/configAI/chapterFileUpdates";
import { useDispatch } from "react-redux";
const signUploadUrlMutation = loader("graphql/mutations/upload/signUploadUrl.graphql");
const fileUploaded = loader("graphql/mutations/upload/fileUploaded.graphql");


const axios = require("axios");

function UploadDocument({
    bookId,
    chapterId,
    organisationId,
    linkFile,
    isInForm,
    onUpload,
}) {
    const [loading, setLoading] = useState(false);
    const [fileSelected, setFileSelected] = useState(false);
    const [file, setFile] = useState(null);
    const [label, setLabel] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const signUploadUrl = useCallback(
        (file) => {
            const { name, type } = file;

            const variables = { organisationId, originalFilename: name, fileType: type };
            return signUploadUrlMutation({ variables });
        },
        [organisationId, signUploadUrlMutation]
    );

    const onUploadFile = useCallback(async () => {
        if (!file) {
            alert(t("files.uploaded.noneFound"));
            return;
        }

        let fileName = "";
        let originalFileName = "";
        let fileType = "";
        const isDev = (!process.env.NODE_ENV || process.env.NODE_ENV === "development");
        const API_BASE_URL = isDev
            ? "http://localhost:1337"
            : window.location.href.includes("acceptance")
                ? "https://acceptance.okcomply.com"
                : "https://my.okcomply.com";

        setLoading(true);

        try {
            const {
                signedUrl,
                filename,
                originalFilename,
                cacheControl,
                serverSideEncryption
            } = await getSignedUploadUrl(file, organisationId).then((res) => res.signUploadUrl);

            const options = {
                headers: {
                    "x-amz-server-side-encryption": serverSideEncryption,
                    "Cache-Control": cacheControl,
                    "Content-Type": file.type
                }
            };

            await axios.put(signedUrl, file, options);
            originalFileName = originalFilename;
            const { type } = file;
            const result = await fileUploadedNew(organisationId, filename, label, type);

            fileName = result.fileUploaded.filename;

            fileType = result.fileUploaded.fileType;
            const { version } = result.fileUploaded.versions[0];


            const response = await axios.post(`${API_BASE_URL}/invites/openai-rag-upload`, {
                fileName,
                originalFilename,
                version,
                fileType,
                chapterId,
                bookId
            });

            linkFile(result.data.fileUploaded);
            onCloseForm();
        } catch (err) {
            setLoading(false);
        }
        let fullFile = { filename: fileName, originalFileName, fileType };
        onUpload(fullFile);
        dispatch(addChapterFile(chapterId, { filename: fileName, originalFileName, fileType }));
    }, [file, label, t, signUploadUrl, fileUploaded, linkFile]);

    const onChange = useCallback((event) => {
        const files = Array.from(event.target.files);
        const selected = files[0];
        event.target.value = null;
        if (selected) {
            const fileParts = selected.name.split(".");
            fileParts.pop();
            const defaultLabel = fileParts.join(".");
            setFileSelected(true);
            setFile(selected);
            setLabel(defaultLabel);
        }
    }, []);

    const onFileLabelChanged = (event) => setLabel(event.target.value);

    const onOpenForm = () => {
        setLoading(false);
        setFileSelected(false);
        setFile(null);
        setIsOpen(true);
    };

    const onCloseForm = () => {
        setLoading(false);
        setFileSelected(false);
        setFile(null);
        setIsOpen(false);
    };

    return (
        <>
            {!isOpen ? (
                <ButtonAdd
                    dark={isInForm}
                    label={t("file.add")}
                    onClick={onOpenForm}
                />
            ) : (
                <FormContainer
                    isInForm={isInForm}
                    title={t("file.addSingle")}
                    onClose={onCloseForm}
                >
                    <AddDocumentButton
                        fileSelected={fileSelected}
                        fileName={file?.name}
                        light={!isInForm}
                        loading={loading}
                        onChange={onChange}
                    />
                    <div className="spacer" />
                    <div className="container-file-label">
                        {fileSelected && (
                            <>
                                <InputText
                                    name="label"
                                    label={t("file.label")}
                                    onChange={onFileLabelChanged}
                                    defaultValue={label}
                                    dark={!isInForm}
                                />
                                <div className="spacer" />
                            </>
                        )}
                        <div className="container-buttons">
                            <Button
                                outlined
                                dark={!isInForm}
                                label={t("button.cancel.label")}
                                onClick={onCloseForm}
                            />
                            <Button
                                dark={!isInForm}
                                spacedLeft
                                secondary
                                label={t("file.button.add")}
                                onClick={onUploadFile}
                            />
                        </div>
                    </div>
                </FormContainer>
            )}
        </>
    );
}

UploadDocument.propTypes = {
    organisationId: PropTypes.string.isRequired,
    signUploadUrlMutation: PropTypes.func.isRequired,
    fileUploadedMutation: PropTypes.func.isRequired,
    linkFile: PropTypes.func.isRequired,
    className: PropTypes.string,
    t: PropTypes.func.isRequired,
    isInForm: PropTypes.bool
};

export default UploadDocument;

export async function getSignedUploadUrl(file, organisationId) {
    const { name, type } = file;
    const variables = { organisationId, originalFilename: name, fileType: type };

    const { data } = await client.mutate({
        mutation: signUploadUrlMutation,
        variables,
    });
    return data;
}

export async function fileUploadedNew(organisationId, filename, label, fileType) {
    const variables = { organisationId, filename, label, fileType };

    try {
        const { data } = await client.mutate({
            mutation: fileUploaded,
            variables,
        });
        return data;
    } catch (error) {
        console.error("File upload mutation failed", error);
        throw error;
    }
}